import * as Sentry from '@sentry/browser';
import * as React from 'react';
import {fetchCurrentUser, getAccessToken} from '~/modules/Profile/api';
import {LocalStoreKeys} from '~/modules/Profile/constants';
import {IUser} from '~/modules/Users/models/User';
import {PermissionResourceType} from '~/modules/Users/models/UserPermission';

type Action =
    | { type: 'signIn'; user: IUser }
    | { type: 'signOut' }
    | { type: 'selectAgency'; agencyId: number }
    | { type: 'selectSeller'; sellerId: number }
    | { type: 'selectImpersonatedUser'; userId: number | null }
    | { type: 'selectImpersonatedResource'; resourceId: number | null };

export type ISessionRoles = {
    admin: boolean;
    seller: boolean;
    buyer: boolean;
    api: boolean;
    inactive: boolean;
    manager: boolean;
};

export type ISession = {
    user: IUser;
    isPending: boolean;
    selectedAgencyId: number | null;
    selectedSellerId: number | null;
    selectedImpersonatedUserId: number | null;
    selectedImpersonatedResourceId: number | null;
};

export type ISessionContext = {
    session: ISession;
    signIn?: (email: string, password: string) => Promise<IUser>;
    signOut?: () => void;
    setSelectedAgency?: (agencyId: number) => void;
    setSelectedSeller?: (sellerId: number) => void;
    setSelectedImpersonatedUser?: (userId: number | null) => void;
    setSelectedImpersonatedResource?: (resourceId: number | null) => void;
};

const sessionInitialState = {
    isPending: true,
    selectedAgencyId: null,
    selectedSellerId: null,
    selectedImpersonatedUserId: null,
    selectedImpersonatedResourceId: null,
} as ISession;

export const SessionContext = React.createContext<ISessionContext>({session: sessionInitialState});

export const useSession = (): ISessionContext => React.useContext(SessionContext);

const sessionReducer = (state: ISession, action: Action): ISession => {
    switch (action.type) {
        case 'signOut':
            Sentry.setUser(null);
            return {...sessionInitialState, isPending: false};
        case 'signIn': {
            Sentry.setUser(action.user);
            const firstAgency = action.user.permissions?.find(p => p.resourceType === PermissionResourceType.AGENCY);
            const firstSeller = action.user.permissions?.find(p => p.resourceType === PermissionResourceType.SELLER);
            return {
                ...state,
                user: action.user,
                isPending: false,
                selectedAgencyId: firstAgency?.resourceId ?? null,
                selectedSellerId: firstSeller?.resourceId ?? null,
            };
        }
        case 'selectAgency':
            return {...state, selectedAgencyId: action.agencyId};
        case 'selectSeller':
            return {...state, selectedSellerId: action.sellerId};
        case 'selectImpersonatedUser':
            return {...state, selectedImpersonatedUserId: action.userId, selectedImpersonatedResourceId: null};
        case 'selectImpersonatedResource':
            return {...state, selectedImpersonatedResourceId: action.resourceId};
    }
    return state;
};

export const SessionProvider: React.FC<React.PropsWithChildren> = ({children}) => {
    let [session, dispatch] = React.useReducer(sessionReducer, sessionInitialState);
    React.useEffect(() => {
        fetchCurrentUser().then(user => {
            if (user) {
                dispatch({type: 'signIn', user});
            }
        }).catch(() => {
            window.localStorage.removeItem(LocalStoreKeys.AccessToken);
            dispatch({type: 'signOut'});
        });
    }, []);
    return <SessionContext.Provider value={{
        session,
        setSelectedAgency: (agencyId: number) => dispatch({type: 'selectAgency', agencyId}),
        setSelectedSeller: (sellerId: number) => dispatch({type: 'selectSeller', sellerId}),
        setSelectedImpersonatedUser: (userId: number | null) => dispatch({type: 'selectImpersonatedUser', userId}),
        setSelectedImpersonatedResource: (resourceId: number | null) => dispatch({type: 'selectImpersonatedResource', resourceId}),
        signIn: (email: string, password: string): Promise<IUser> => getAccessToken(email, password).then<IUser>(async (accessToken) => {
            window.localStorage.setItem(LocalStoreKeys.AccessToken, accessToken);
            return await fetchCurrentUser().then<IUser>(user => {
                dispatch({type: 'signIn', user});
                return user;
            }).catch(error => {
                window.localStorage.setItem(LocalStoreKeys.AccessToken, accessToken);
                dispatch({type: 'signOut'});
                throw error;
            });
        }),
        signOut: () => {
            window.localStorage.removeItem(LocalStoreKeys.AccessToken);
            dispatch({type: 'signOut'});
        }
    }}
    >{children}</SessionContext.Provider>;
};
